/**
 * T-085 — tnd-support distribution GUI: shared API contracts.
 *
 * Single source of truth for the wire format between the Cloudflare Pages
 * Functions backend (functions/api/**, worker/**) and the Vite SPA frontend
 * (src/**). Both sides import these types; DTOs are never redefined locally.
 *
 * Conventions:
 *  - Every JSON response body is one of the *Response interfaces below or
 *    {@link ApiErrorBody}; never a bare array or ad-hoc object.
 *  - All timestamps on the wire are ISO-8601 UTC strings (e.g. D1
 *    `strftime('%Y-%m-%dT%H:%M:%fZ','now')` output).
 *  - All API routes require `Authorization: Bearer <SUPPORT_APP_TOKEN>` except
 *    `GET /api/download/<token>`, where the link token itself is the
 *    capability (spec §3, §4).
 *  - All API responses carry `Cache-Control: no-store` (spec §3); the
 *    worker/http.ts helpers enforce this.
 *  - Secrets (SUPPORT_APP_TOKEN, GCP service-account material, GITHUB_TOKEN,
 *    GCS OAuth tokens, raw link tokens after creation) NEVER appear in any
 *    response body (spec §3).
 */

/* ------------------------------------------------------------------------ */
/* Enumerations                                                              */
/* ------------------------------------------------------------------------ */

/** Concrete artifact provider. An artifact always has exactly one source. */
export type ArtifactProvider = 'gcs' | 'github';
export const ARTIFACT_PROVIDERS: readonly ArtifactProvider[] = ['gcs', 'github'];

/**
 * Release source (spec §5: "normalized semver, release date, source").
 * `merged` means the version was reconciled from both GCS frozen `v/`
 * prefixes and GitHub Releases. Mirrors release_cache.provider in D1.
 */
export type ReleaseSource = ArtifactProvider | 'merged';
export const RELEASE_SOURCES: readonly ReleaseSource[] = ['gcs', 'github', 'merged'];

/** Python ABI selectors for artifacts and the command generator (spec §5). */
export type PythonAbi = 'py3.10' | 'py3.12';
export const PYTHON_ABIS: readonly PythonAbi[] = ['py3.10', 'py3.12'];

/**
 * Reconciled artifact taxonomy (spec §5 Page 1 table rows; arbitrated by Main
 * 2026-07-28 as the D1 artifact_cache.kind CHECK set).
 */
export type ArtifactKind =
  | 'core_deb'
  | 'trt_plugin_deb'
  | 'frozen_zip'
  | 'stable_zip'
  | 'scheduler_wheel'
  | 'decoder_tarball';
export const ARTIFACT_KINDS: readonly ArtifactKind[] = [
  'core_deb',
  'trt_plugin_deb',
  'frozen_zip',
  'stable_zip',
  'scheduler_wheel',
  'decoder_tarball',
];

/** Serving targets for tnd-setup command generation (spec §5 Page 2). */
export type ServingTarget = 'vllm' | 'trtllm' | 'native' | 'all';
export const SERVING_TARGETS: readonly ServingTarget[] = ['vllm', 'trtllm', 'native', 'all'];
/** CLI flag emitted for each serving target in generated commands. */
export const SERVING_TARGET_FLAGS: Record<ServingTarget, string> = {
  vllm: '--vllm',
  trtllm: '--trtllm',
  native: '--native',
  all: '--all',
};

/**
 * Download-link issuance modes (spec §4). Mirrors download_links.mode in D1.
 *  - `managed`: opaque token, proxied via /api/download/<token>, revocable.
 *  - `gcs_v4`:  raw GCS V4 signed URL; non-revocable after issuance.
 */
export type LinkMode = 'managed' | 'gcs_v4';
export const LINK_MODES: readonly LinkMode[] = ['managed', 'gcs_v4'];

/**
 * Server-bounded expiry choices (spec §3). Clients MUST pick one of these;
 * arbitrary client-supplied expirations are rejected with
 * `expiry_out_of_bounds`.
 */
export type LinkExpiry = '1h' | '24h' | '7d';
export const LINK_EXPIRIES: readonly LinkExpiry[] = ['1h', '24h', '7d'];
export const LINK_EXPIRY_SECONDS: Record<LinkExpiry, number> = {
  '1h': 3_600,
  '24h': 86_400,
  '7d': 604_800,
};

/**
 * Derived lifecycle state of a download link. Not stored in D1; computed from
 * `revoked_at` / `expires_at` via {@link deriveLinkStatus}.
 */
export type LinkStatus = 'active' | 'expired' | 'revoked';
export const LINK_STATUSES: readonly LinkStatus[] = ['active', 'expired', 'revoked'];

/**
 * Canonical status derivation. Revocation wins over expiry; a link whose
 * expiry instant has passed is `expired`; otherwise `active`.
 */
export function deriveLinkStatus(
  link: { revokedAt: string | null; expiresAt: string },
  now: Date = new Date(),
): LinkStatus {
  if (link.revokedAt !== null) return 'revoked';
  return Date.parse(link.expiresAt) <= now.getTime() ? 'expired' : 'active';
}

/** Allowlisted prefixes for the object browser, GET /api/objects (spec §5). */
export type ObjectPrefix = 'debs' | 'releases';
export const OBJECT_PREFIXES: readonly ObjectPrefix[] = ['debs', 'releases'];

/**
 * Application audit event types (spec §5 Page 3). Mirrors the
 * audit_events.event_type CHECK set in D1.
 */
export type AuditEventType =
  | 'issue'
  | 'download'
  | 'revoke'
  | 'expire'
  | 'provider_error'
  | 'auth_failure';
export const AUDIT_EVENT_TYPES: readonly AuditEventType[] = [
  'issue',
  'download',
  'revoke',
  'expire',
  'provider_error',
  'auth_failure',
];

/* ------------------------------------------------------------------------ */
/* GCS object-path allowlist (spec §3)                                       */
/* ------------------------------------------------------------------------ */

/**
 * Server-enforced outer gate for browser-supplied object paths. Anything not
 * matching one of these shapes is rejected with `forbidden_path`. Reconciling
 * the final path component against known artifact names happens in the worker
 * (worker/artifacts.ts); these patterns only bound the namespace.
 */
export const GCS_DEB_OBJECT_PATH_PATTERN =
  /^debs\/run-\d+\/py3\.(?:10|12)\/[A-Za-z0-9][A-Za-z0-9._+-]*\.deb$/;
export const GCS_RELEASE_OBJECT_PATH_PATTERN =
  /^releases\/inference-engine\/v\/\d+\.\d+\.\d+(?:-[A-Za-z0-9.-]+)?(?:\+[A-Za-z0-9.-]+)?\/py3\.(?:10|12)\/[A-Za-z0-9][A-Za-z0-9._+-]*$/;

/** True when `objectPath` matches the spec §3 GCS allowlist. */
export function isAllowlistedObjectPath(objectPath: string): boolean {
  return (
    GCS_DEB_OBJECT_PATH_PATTERN.test(objectPath) ||
    GCS_RELEASE_OBJECT_PATH_PATTERN.test(objectPath)
  );
}

/* ------------------------------------------------------------------------ */
/* Page 1 — release selector                                                 */
/* ------------------------------------------------------------------------ */

/** GET /api/releases → {@link ReleasesResponse} */
export interface ReleaseSummary {
  /** Normalized semver without a leading `v` (e.g. "1.4.2"). */
  version: string;
  source: ReleaseSource;
  /** ISO-8601 release/publish timestamp; null when the provider has none. */
  releaseDate: string | null;
  /** True when at least one installable artifact reconciled for this version. */
  available: boolean;
}

/**
 * A provider that could not be reached during the last reconciliation.
 *
 * Reconciliation treats GCS as required and GitHub as best-effort: a GitHub
 * outage (or an expired/rotated `GITHUB_TOKEN` — HTTP 401 "Bad credentials")
 * degrades the release list to the GCS half rather than failing the whole
 * request. The UI renders these so a partial list is never mistaken for a
 * complete one. `message` is the already-normalized, safe-to-display provider
 * message (never a token or raw upstream payload).
 */
export interface ProviderWarning {
  provider: ArtifactProvider;
  /** Mirrors {@link ApiErrorCode} classification of the underlying failure. */
  code: 'provider_error' | 'rate_limited';
  message: string;
}

export interface ReleasesResponse {
  releases: ReleaseSummary[];
  /**
   * Non-fatal provider degradations behind this response. Absent/empty means
   * every provider answered; a `github` entry means the list is GCS-only.
   */
  warnings?: ProviderWarning[];
}

/**
 * Artifact checksums (spec §5). `sha256Hex` is populated ONLY from a sibling
 * `.sha256` object or a trusted GitHub checksum — never derived from MD5 or
 * CRC32C.
 */
export interface ArtifactChecksums {
  /** Base64 MD5, from the GCS JSON API `md5Hash` field. */
  md5Base64?: string;
  /** Base64 CRC32C, from the GCS JSON API `crc32c` field. */
  crc32cBase64?: string;
  /** Lowercase hex SHA-256 from a `.sha256` object or GitHub checksum only. */
  sha256Hex?: string;
}

/** GET /api/releases/:version/artifacts → {@link ReleaseArtifactsResponse} */
export interface Artifact {
  /** Stable artifact id (artifact_cache.id). */
  id: string;
  version: string;
  kind: ArtifactKind;
  provider: ArtifactProvider;
  pythonAbi: PythonAbi | null;
  /**
   * Allowlisted GCS object path (`debs/…`, `releases/…`) or a GitHub release
   * asset name. Never arbitrary client input.
   */
  objectPath: string;
  sizeBytes: number | null;
  /** ISO-8601 provider last-modified timestamp. */
  updatedAt: string;
  checksums: ArtifactChecksums;
  /**
   * Provider-published download URL when one exists without issuance —
   * GitHub's `browser_download_url` for `github` artifacts. Always null for
   * `gcs` artifacts: the bucket is private, so a GCS artifact is only
   * downloadable through an issued link (POST /api/links). Mirrors
   * artifact_cache.public_url.
   */
  publicUrl: string | null;
}

/**
 * GCS object-path prefix holding every artifact of one release version
 * (`releases/inference-engine/v/<version>/`). Used to scope an issued-link
 * lookup to a single release — see {@link LinksListQuery.objectPathPrefix}.
 *
 * `version` must already be normalized (no leading `v`); the caller is the
 * release list, whose versions come from reconciliation.
 */
export function gcsReleaseObjectPrefix(version: string): string {
  return `releases/inference-engine/v/${version}/`;
}

/**
 * Object-path prefixes the API accepts for {@link LinksListQuery.objectPathPrefix}.
 * A prefix filter is a read-side convenience, not a capability, but it is
 * still bounded to the two namespaces the allowlist covers so it can never be
 * used to probe outside them.
 */
export const OBJECT_PATH_PREFIX_ROOTS: readonly string[] = ['debs/', 'releases/'];

/** True when `prefix` is a non-empty path inside an allowlisted namespace. */
export function isAllowlistedObjectPathPrefix(prefix: string): boolean {
  if (prefix.length === 0 || prefix.length > 512) return false;
  if (prefix.includes('..') || prefix.startsWith('/')) return false;
  return OBJECT_PATH_PREFIX_ROOTS.some((root) => prefix.startsWith(root));
}

export interface ReleaseArtifactsResponse {
  version: string;
  artifacts: Artifact[];
}

/* ------------------------------------------------------------------------ */
/* Page 2 — command generator                                                */
/* ------------------------------------------------------------------------ */

/**
 * Inputs to command generation (spec §5 Page 2). Generation itself is
 * client-side once a download URL has been issued via POST /api/links; this
 * DTO documents the full input set shared by the UI and snapshot tests.
 */
export interface CommandGeneratorInput {
  version: string;
  artifactKind: ArtifactKind;
  pythonAbi: PythonAbi;
  servingTarget: ServingTarget;
  expiry: LinkExpiry;
}

/* ------------------------------------------------------------------------ */
/* Page 3 — access control                                                   */
/* ------------------------------------------------------------------------ */

/**
 * Managed/direct link metadata (spec §5). Raw token values are NEVER present:
 * the token is returned exactly once inside {@link CreateLinkResponse.url} at
 * creation time and only its SHA-256 hash is stored.
 */
export interface LinkSummary {
  id: string;
  mode: LinkMode;
  status: LinkStatus;
  objectPath: string;
  artifactId: string | null;
  createdAt: string;
  expiresAt: string;
  revokedAt: string | null;
  lastUsedAt: string | null;
  downloadCount: number;
}

/** GET /api/links?status=&cursor=&limit=&objectPathPrefix=&artifactId= */
export interface LinksListQuery {
  status?: LinkStatus;
  cursor?: string;
  limit?: number;
  /**
   * Restrict to links whose `objectPath` starts with this prefix — how the
   * release selector lists the links already issued for one version
   * (see {@link gcsReleaseObjectPrefix}). Must satisfy
   * {@link isAllowlistedObjectPathPrefix}; else `invalid_request`.
   */
  objectPathPrefix?: string;
  /** Restrict to links issued against one reconciled artifact id. */
  artifactId?: string;
}

export interface LinksListResponse {
  links: LinkSummary[];
  /** Opaque pagination cursor; null when no further pages exist. */
  nextCursor: string | null;
}

/** POST /api/links — issue a managed link or direct GCS V4 URL (spec §4, §5). */
export interface CreateLinkRequest {
  /** Must satisfy {@link isAllowlistedObjectPath}; else `forbidden_path`. */
  objectPath: string;
  /** Optional reconciliation hint; must exist in artifact_cache when given. */
  artifactId?: string;
  mode: LinkMode;
  expiry: LinkExpiry;
}

export interface CreateLinkResponse {
  link: LinkSummary;
  /**
   * Fully-qualified download URL — `/api/download/<token>` for managed links,
   * the raw GCS V4 signed URL for `gcs_v4`. Returned exactly once, here; it
   * is never listable afterwards.
   */
  url: string;
  /** False for `gcs_v4`: signed URLs are bearer capabilities (spec §4). */
  revocable: boolean;
}

/** POST /api/links/:id/revoke → 200 {@link RevokeLinkResponse}; `gcs_v4` links → 409 `non_revocable`. */
export interface RevokeLinkResponse {
  link: LinkSummary;
}

/* ------------------------------------------------------------------------ */
/* Page 3 — object browser                                                   */
/* ------------------------------------------------------------------------ */

/** GET /api/objects?prefix=debs|releases&cursor=&limit= */
export interface ObjectsListQuery {
  prefix: ObjectPrefix;
  cursor?: string;
  limit?: number;
}

/** Normalized GCS JSON API object metadata (spec §5). */
export interface GcsObjectSummary {
  objectPath: string;
  sizeBytes: number;
  updatedAt: string;
  md5Base64?: string;
  crc32cBase64?: string;
  generation?: string;
}

export interface ObjectsListResponse {
  objects: GcsObjectSummary[];
  nextCursor: string | null;
}

/* ------------------------------------------------------------------------ */
/* Page 3 — audit log                                                        */
/* ------------------------------------------------------------------------ */

/** GET /api/audit?eventType=&linkId=&cursor=&limit= */
export interface AuditListQuery {
  eventType?: AuditEventType;
  linkId?: string;
  cursor?: string;
  limit?: number;
}

/**
 * Privacy-minimized audit event (spec §5, §7). The actor/ip/user-agent SHA-256
 * hashes stored in D1 are internal-only and are intentionally absent here.
 */
export interface AuditEvent {
  id: number;
  eventType: AuditEventType;
  linkId: string | null;
  objectPath: string | null;
  createdAt: string;
  /** Structured detail; never contains tokens, IPs, or credentials. */
  detail: Record<string, unknown> | null;
}

export interface AuditListResponse {
  events: AuditEvent[];
  nextCursor: string | null;
}

/* ------------------------------------------------------------------------ */
/* Page 4 — build run browser                                                */
/* ------------------------------------------------------------------------ */

/** GitHub Actions run statuses (normalized from the REST API). */
export type BuildRunStatus =
  | 'queued'
  | 'in_progress'
  | 'completed'
  | 'waiting'
  | 'requested'
  | 'pending';

/** GitHub Actions run conclusions (present only when status is `completed`). */
export type BuildRunConclusion =
  | 'success'
  | 'failure'
  | 'neutral'
  | 'cancelled'
  | 'skipped'
  | 'timed_out'
  | 'action_required';

/** GET /api/runs?cursor=&limit= — build-package-release.yml runs (spec §5). */
export interface BuildRun {
  id: number;
  runNumber: number;
  name: string | null;
  status: BuildRunStatus;
  conclusion: BuildRunConclusion | null;
  headBranch: string | null;
  headSha: string;
  /** Release version parsed from the run when identifiable; else null. */
  version: string | null;
  startedAt: string | null;
  completedAt: string | null;
  /** GitHub web URL for the run. */
  htmlUrl: string;
}

/** GET /api/runs?cursor=&limit= */
export interface RunsListQuery {
  cursor?: string;
  limit?: number;
}

export interface RunsListResponse {
  runs: BuildRun[];
  nextCursor: string | null;
}

/** GET /api/runs/:id/artifacts — GCS `debs/run-<id>/` inventory (spec §5). */
export interface RunArtifactsResponse {
  run: BuildRun;
  artifacts: Artifact[];
}

/* ------------------------------------------------------------------------ */
/* Download endpoint (capability route, no bearer auth)                      */
/* ------------------------------------------------------------------------ */

/**
 * GET /api/download/<token> streams the object bytes; it is not a JSON route.
 * Failure responses use {@link ApiErrorBody}:
 *  - 404 `not_found` — unknown token;
 *  - 410 `gone`      — link expired or revoked (spec §4).
 */
export const API_DOWNLOAD_BASE_PATH = '/api/download/';

/* ------------------------------------------------------------------------ */
/* Provider health (credential diagnostics)                                  */
/* ------------------------------------------------------------------------ */

/** Outcome of one provider credential probe. */
export type ProviderHealthState =
  /** Credentials accepted and the probe returned data. */
  | 'ok'
  /** Provider rejected the credential (GitHub 401/403, GCS OAuth failure). */
  | 'unauthorized'
  /** Provider reachable but rate limited. */
  | 'rate_limited'
  /** Any other reachability/transport failure. */
  | 'unreachable'
  /** No credential is configured for this provider. */
  | 'not_configured';

/**
 * One provider's live credential status. Never carries a token: `credential`
 * is a non-reversible descriptor (token prefix class + length) that lets an
 * operator confirm *which* credential is live after a rotation without
 * revealing it.
 */
export interface ProviderHealth {
  provider: ArtifactProvider;
  state: ProviderHealthState;
  /** Safe-to-display detail, e.g. `HTTP 401 - Bad credentials`. */
  detail: string;
  /**
   * Non-reversible fingerprint of the credential actually in use, e.g.
   * `github_pat_… (93 chars)` or `ghp_… (40 chars)`; null when none is
   * configured. Only the documented public prefix and the length are shown.
   */
  credential: string | null;
  /**
   * GitHub only: the `x-oauth-scopes` response header, when GitHub sends one
   * (classic PATs only — fine-grained PATs report an empty string). Lets an
   * operator verify the granted scopes without leaving the GUI.
   */
  scopes?: string | null;
}

/** GET /api/providers/health — credential diagnostics for both providers. */
export interface ProvidersHealthResponse {
  providers: ProviderHealth[];
  /** ISO-8601 instant the probes ran. */
  checkedAt: string;
}

/* ------------------------------------------------------------------------ */
/* Error envelope                                                            */
/* ------------------------------------------------------------------------ */

/**
 * Stable machine-readable error codes. Every non-2xx API response body is an
 * {@link ApiErrorBody} carrying one of these codes; provider failures are
 * normalized to `provider_error` and never leak credentials, request headers,
 * or raw upstream payloads (spec §3).
 */
export type ApiErrorCode =
  /** 401 — missing/invalid bearer token. */
  | 'unauthorized'
  /** 400 — malformed request body/params. */
  | 'invalid_request'
  /** 403 — object path outside the spec §3 allowlist. */
  | 'forbidden_path'
  /** 400 — expiry not one of {@link LINK_EXPIRIES}. */
  | 'expiry_out_of_bounds'
  /** 404 — unknown resource id / download token. */
  | 'not_found'
  /** 409 — revoke attempted on a `gcs_v4` (non-revocable) link. */
  | 'non_revocable'
  /** 410 — download link expired or revoked. */
  | 'gone'
  /** 429 — upstream or server rate limit. */
  | 'rate_limited'
  /** 502 — normalized GCS/GitHub/OAuth provider failure. */
  | 'provider_error'
  /** 500 — unexpected server failure. */
  | 'internal_error';

/** Canonical HTTP status for each error code (worker and frontend agree). */
export const API_ERROR_STATUS: Record<ApiErrorCode, number> = {
  unauthorized: 401,
  invalid_request: 400,
  forbidden_path: 403,
  expiry_out_of_bounds: 400,
  not_found: 404,
  non_revocable: 409,
  gone: 410,
  rate_limited: 429,
  provider_error: 502,
  internal_error: 500,
};

export interface ApiErrorBody {
  error: {
    code: ApiErrorCode;
    /** Human-readable, safe-to-display message; never contains secrets. */
    message: string;
  };
}

/** Type guard for distinguishing error bodies from success DTOs client-side. */
export function isApiErrorBody(body: unknown): body is ApiErrorBody {
  if (typeof body !== 'object' || body === null) return false;
  const err = (body as { error?: unknown }).error;
  if (typeof err !== 'object' || err === null) return false;
  const { code, message } = err as { code?: unknown; message?: unknown };
  return typeof code === 'string' && typeof message === 'string';
}
